import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ConfigProvider } from "antd";
import { ApolloProvider } from "@apollo/client";
import { AuthProvider, AuthContextConsumer } from "./components/auth";

// Apollo
import { getApolloClient } from "./graphql/client/apollo-client";

// CSS
// Note: Ant Design component styles are auto-imported by vite-plugin-imp
// Import order matters: global first, then variables, then client overrides
import "./global.css";
import "./variables.css";
import "./client.css";

// Explicitly import Modal and Notification LESS for hook methods - this will be compiled with theme variables
import "antd/es/modal/style/index.less";
import "antd/es/notification/style/index.less";

// Initialize custom toast notifications for React 19 compatibility
import { initToastContainer } from "./design-system/components/toast-notifications/toast-container";
initToastContainer();

import { Auth0ContextInterface, User } from "@auth0/auth0-react";
import { NotificationSubscriptionProvider } from "./components/subscriptions/notification-subscription-provider";
import { createNetworkStatusNotifier } from "react-apollo-network-status";
import { CrudUserFeedbackContainer } from "./containers/crud-user-feedback";
import { GlobalContextProvider } from "./context/global-context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TaxonomyProvider } from "./context/taxonomy-context";
import { LDProvider } from "launchdarkly-react-client-sdk";

import App from "./containers/app";

const { link: apolloLinkNetworkStatus, useApolloNetworkStatusReducer } =
  createNetworkStatusNotifier();

const root = document.getElementById("root");

const queryClient = new QueryClient();

createRoot(root!).render(
  <BrowserRouter>
    <AuthProvider>
      <AuthContextConsumer>
        {(auth0Ctx: Auth0ContextInterface<User>) => (
          <LDProvider
            clientSideID={import.meta.env.VITE_LD_CLIENT_SIDE_ID}
            deferInitialization={true}
            context={
              auth0Ctx.isAuthenticated && auth0Ctx.user
                ? {
                    kind: "user",
                    name: auth0Ctx.user.name || auth0Ctx.user.email,
                    key: auth0Ctx.user.sub,
                    email: auth0Ctx.user.email,
                  }
                : undefined
            }
          >
            <ApolloProvider client={getApolloClient(apolloLinkNetworkStatus, auth0Ctx)}>
              <NotificationSubscriptionProvider>
                <ConfigProvider input={{ autoComplete: "off" }}>
                  <QueryClientProvider client={queryClient}>
                    <CrudUserFeedbackContainer
                      useApolloNetworkStatusReducer={useApolloNetworkStatusReducer}
                    />
                    <GlobalContextProvider>
                      <TaxonomyProvider>
                        <App />
                      </TaxonomyProvider>
                    </GlobalContextProvider>
                  </QueryClientProvider>
                </ConfigProvider>
              </NotificationSubscriptionProvider>
            </ApolloProvider>
          </LDProvider>
        )}
      </AuthContextConsumer>
    </AuthProvider>
  </BrowserRouter>
);
